import React, { useState, useEffect } from 'react';
import { 
  FiEye,        // View icon
  FiRefreshCw,  // Refresh icon
  FiCheckCircle, // Status icon
  FiAlertCircle  // Alert icon
} from 'react-icons/fi';
import { IoSearchOutline } from 'react-icons/io5';

const StudentFollowups = () => {
  const [followups, setFollowups] = useState([]);
  const [activities, setActivities] = useState({});
  const [students, setStudents] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFollowup, setSelectedFollowup] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ 
    key: 'followup_date', 
    direction: 'desc' 
  });

  // Fetch comprehensive followup data
  const fetchFollowupData = async (signal) => {
    setLoading(true);
    try {
      // Fetch all followups
      const followupsResponse = await fetch('https://apissc.sainikschoolcadet.com/api/activity-followup', { signal });
      const followupsData = await followupsResponse.json();

      // Create maps to store activities and students
      const activitiesMap = {};
      const studentsMap = {};

      // Use Promise.all to fetch activity data in parallel
      await Promise.all(followupsData.map(async (followup) => {
        try {
          // Fetch activity details
          const activityResponse = await fetch(`https://apissc.sainikschoolcadet.com/api/student-activity/${followup.activity_id}`, { signal });
          const activityData = await activityResponse.json();
          
          // Store activity data
          activitiesMap[followup.activity_id] = activityData;
          
          // Make sure s_id exists and is valid before fetching student
          if (activityData && activityData.s_id) {
            // Fetch student details
            const studentResponse = await fetch(`https://apissc.sainikschoolcadet.com/api/students/getbyid/${activityData.s_id}`, { signal });
            const studentData = await studentResponse.json();
            
            // Store student data properly
            if (studentData) {
              studentsMap[activityData.s_id] = studentData;
            } else {
              console.error(`No student data returned for ID: ${activityData.s_id}`);
            }
          } else {
            console.error(`Invalid or missing s_id in activity data for activity ID: ${followup.activity_id}`);
          }
        } catch (err) {
          if (err.name === 'AbortError') throw err;
          console.error(`Error fetching data for followup ${followup.id}:`, err);
        }
      }));

      if (signal && signal.aborted) return;

      setFollowups(followupsData);
      setActivities(activitiesMap);
      setStudents(studentsMap);
      setLoading(false);
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Error fetching followup data:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  // Initial data fetch
  useEffect(() => {
    const controller = new AbortController();
    fetchFollowupData(controller.signal);

    return () => {
      controller.abort();
    };
  }, []);

  const getFollowupTag = (dateString) => {
    const today = new Date();
    const followupDate = new Date(dateString);
  
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const startOfTomorrow = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1);
  
    if (followupDate >= startOfTomorrow) return 'Upcoming';
    if (followupDate >= startOfToday && followupDate < startOfTomorrow) return 'Today';
    return 'Passed';
  };

  // Format date function
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Sorting function
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfTomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  
  const sortedFollowups = [...followups].sort((a, b) => {
    const dateA = new Date(a.followup_date);
    const dateB = new Date(b.followup_date);
  
    const tagA = getFollowupTag(a.followup_date);
    const tagB = getFollowupTag(b.followup_date);
  
    const tagOrder = { 'Upcoming': 0, 'Today': 1, 'Passed': 2 };
  
    if (tagOrder[tagA] !== tagOrder[tagB]) {
      return tagOrder[tagA] - tagOrder[tagB]; // Sort by tag priority
    }
  
    // Same tag - apply internal sorting
    if (tagA === 'Upcoming') return dateA - dateB; // Nearest first
    if (tagA === 'Today') return 0; // Equal priority
    if (tagA === 'Passed') return dateB - dateA; // Most recent first
  
    return 0;
  });
  
  // Filtering function
  const filteredFollowups = sortedFollowups.filter(followup => {
    const activity = activities[followup.activity_id] || {};
    const student = activity.s_id ? students[activity.s_id] || {} : {};

    const matchesStatus = filter === 'all' || followup.status === filter;
    const matchesSearch = 
      student.f_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      activity.lead_label?.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesStatus && matchesSearch;
  });
  
  // Sort handler
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'desc' 
        ? 'asc' 
        : 'desc'
    }));
  };

  // Debug function to help identify issues - can be removed in production
  const debugStudent = (activity_id) => {
    const activity = activities[activity_id] || {};
    const s_id = activity.s_id;
    const student = s_id ? students[s_id] : null;
    
    console.log(`Activity ID: ${activity_id}`);
    console.log(`Student ID (s_id): ${s_id}`);
    console.log('Activity Data:', activity);
    console.log('Student Data:', student);
    
    return student;
  };

  return (
    <div className="bg-white rounded-2xl shadow-md p-6">
      {/* Header Section */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-gray-800">Student Followups</h2>
        <div className="flex items-center space-x-4">
          <button 
            onClick={fetchFollowupData}
            className="text-gray-600 hover:text-blue-600 transition-colors"
            title="Refresh"
          >
            <FiRefreshCw size={20} />
          </button>
        </div>
      </div>

      {/* Stats Section */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-blue-50 p-4 rounded-lg">
          <p className="text-sm text-gray-600 mb-2">Total Followups</p>
          <p className="text-2xl font-bold text-blue-600">{followups.length}</p>
        </div>
        <div className="bg-green-50 p-4 rounded-lg">
          <p className="text-sm text-gray-600 mb-2">Completed</p>
          <p className="text-2xl font-bold text-green-600">
            {followups.filter(f => f.status === 'complete').length}
            <span className="text-sm text-gray-500">
              {` (${Math.round((followups.filter(f => f.status === 'complete').length / followups.length) * 100) || 0}%)`}
            </span>
          </p>
        </div>
        <div className="bg-yellow-50 p-4 rounded-lg">
          <p className="text-sm text-gray-600 mb-2">Incomplete</p>
          <p className="text-2xl font-bold text-yellow-600">
            {followups.filter(f => f.status === 'incomplete').length}
            <span className="text-sm text-gray-500">
              {` (${Math.round((followups.filter(f => f.status === 'incomplete').length / followups.length) * 100) || 0}%)`}
            </span>
          </p>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center space-x-4">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="border border-gray-300 px-3 py-2 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            <option value="all">All Followups</option>
            <option value="complete">Completed</option>
            <option value="incomplete">Incomplete</option>
          </select>
          <div className="relative">
            <input 
              type="text"
              placeholder="Search Student or Lead Label"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="border border-gray-300 px-3 py-2 rounded-lg pl-8 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            <IoSearchOutline className="h-5 w-5 absolute left-2 top-3 text-gray-400" />
          </div>
        </div>
      </div>

      {/* Loading and Error States */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-blue-500"></div>
        </div>
      ) : error ? (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">S.No.</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lead Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lead Label</th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort('followup_date')}
                >
                  <div className="flex items-center">
                    Followup Date
                    {sortConfig.key === 'followup_date' && (
                      <span className="ml-2">
                        {sortConfig.direction === 'asc' ? '▲' : '▼'}
                      </span>
                    )}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Followup Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Followup Tag</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredFollowups.map((followup, index) => {
                const activity = activities[followup.activity_id] || {};
                const student = activity.s_id ? students[activity.s_id] || {} : {};
                const followupTag = getFollowupTag(followup.followup_date);

                return (
                  <tr key={followup.id} className="hover:bg-gray-50">
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{index + 1}</td>

                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 flex-shrink-0 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                          {student.f_name ? student.f_name.charAt(0).toUpperCase() : '?'}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900">{student.f_name || 'Unknown Student'}</div>
                          <div className="text-sm text-gray-500">{student.phonenumber || 'N/A'}</div>
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                        activity.lead_status === 'Interested' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {activity.lead_status || 'N/A'}
                      </span>
                    </td>

                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {activity.lead_label || 'N/A'}
                    </td>

                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(followup.followup_date)}
                    </td>

                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                        followup.status === 'complete' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {followup.status}
                      </span>
                    </td>

                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                        followupTag === 'Upcoming'
                          ? 'bg-blue-100 text-blue-800'
                          : followupTag === 'Today'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {followupTag}
                      </span>
                    </td>

                    <td className="px-4 py-4 whitespace-nowrap text-center">
                      <button
                        onClick={() => {
                          // Pass s_id instead of just followup.id
                          const s_id = activity.s_id;
                          if (s_id) {
                            window.open(`/view-student/${s_id}`, '_blank');
                          } else {
                            console.error(`No s_id found for activity ${followup.activity_id}`);
                            alert("Student ID not found for this followup");
                          }
                        }}
                        className="text-blue-500 hover:text-blue-700 p-2 rounded-full hover:bg-blue-50"
                        title="View Details"
                      >
                        <FiEye size={18} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* No Data */}
          {filteredFollowups.length === 0 && (
            <div className="text-center py-10 text-gray-500">No followups found.</div>
          )}
        </div>
      )}
      
      {/* Pagination */}
      {filteredFollowups.length > 0 && (
        <div className="flex items-center justify-between mt-4">
          <div>
            <p className="text-sm text-gray-700">
              Showing 1 to {filteredFollowups.length} of {filteredFollowups.length} results
            </p>
          </div>
          <div className="flex space-x-1">
            <button className="px-3 py-1 rounded border border-gray-300 bg-white text-gray-500 hover:bg-gray-50">
              &lt;
            </button>
            <button className="px-3 py-1 rounded border border-blue-500 bg-blue-50 text-blue-600">
              1
            </button>
            <button className="px-3 py-1 rounded border border-gray-300 bg-white text-gray-500 hover:bg-gray-50">
              &gt;
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentFollowups;